import { createSlice } from "@reduxjs/toolkit";

const emptyResourceHolder = {
  meta: {
    count: 0,
    lastFetched: null,
  },
  list: [],
};

const initialState = {
  hasRunFetchCheck: false,
  pbx: {
    context: "",
    id: undefined,
    provisioningUrls: [],
  },
  apiTokens: { ...emptyResourceHolder },
  accounts: { ...emptyResourceHolder },

  callLog: { ...emptyResourceHolder },
  flexDeskLog: { ...emptyResourceHolder },
  queueLog: { ...emptyResourceHolder },
  queueMemberLog: { ...emptyResourceHolder },
  webhookLog: { ...emptyResourceHolder },

  allUsers: { ...emptyResourceHolder },
  blocklists: { ...emptyResourceHolder },
  callParkings: { ...emptyResourceHolder },
  conferences: { ...emptyResourceHolder },
  customDestinations: { ...emptyResourceHolder },
  dayNights: { ...emptyResourceHolder },
  dispatchers: { ...emptyResourceHolder },
  distinctiveRingtones: { ...emptyResourceHolder },
  groups: { ...emptyResourceHolder },
  holidaySets: { ...emptyResourceHolder },
  images: { ...emptyResourceHolder },
  inboundRoutes: { ...emptyResourceHolder },
  intercoms: { ...emptyResourceHolder },
  ivrs: { ...emptyResourceHolder },
  lineKeyTemplates: { ...emptyResourceHolder },
  mobile: { ...emptyResourceHolder },
  musicOnHolds: { ...emptyResourceHolder },
  outboundRules: { ...emptyResourceHolder },
  phonebooks: { ...emptyResourceHolder },
  provisionings: { ...emptyResourceHolder },
  queues: { ...emptyResourceHolder },
  ringtones: { ...emptyResourceHolder },
  sounds: { ...emptyResourceHolder },
  timetables: { ...emptyResourceHolder },
  users: { ...emptyResourceHolder },
  voicemails: { ...emptyResourceHolder },
  webhooks: { ...emptyResourceHolder },

  provisionableAccounts: { ...emptyResourceHolder },
  provisionablePhones: { ...emptyResourceHolder },
  queueMembers: { ...emptyResourceHolder },
  phoneNumbers: { ...emptyResourceHolder },
  availablePhoneNumbers: { ...emptyResourceHolder },
};

const { actions, reducer } = createSlice({
  name: "domain",
  initialState,
  reducers: {
    setPbxWithReset(state, action) {
      return {
        ...initialState,
        pbx: action.payload.pbx,
      };
    },
    setPbx(state, action) {
      state.pbx = action.payload.pbx;
    },
    setHasRunFetchCheck(state) {
      state.hasRunFetchCheck = true;
    },
    setApiTokenStore: (state, action) => {
      state.apiTokens = {
        list: action.payload.tokens,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setPbxAccounts: (state, action) => {
      state.accounts = {
        list: action.payload.accounts,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },

    // Logs
    setCallLog(state, action) {
      state.callLog = {
        list: action.payload.cdr.map((item) => item[Object.keys(item)[0]]),
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setFlexDeskLog(state, action) {
      state.flexDeskLog = {
        list: action.payload.fdl,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setQueueLog(state, action) {
      state.queueLog = {
        list: action.payload.qdr.map((item) => item[Object.keys(item)[0]]),
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setQueueMemberLog(state, action) {
      state.queueMemberLog = {
        list: action.payload.qml,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setWebhookLog(state, action) {
      state.webhookLog = {
        list: action.payload.wel,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },

    // Lists
    setAllUserStore(state, action) {
      state.allUsers = {
        list: action.payload.sippeers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setBlocklistStore(state, action) {
      state.blocklists = {
        list: action.payload.blocklists,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setCallParkingStore(state, action) {
      state.callParkings = {
        list: action.payload.parkingLots,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setConferenceStore(state, action) {
      state.conferences = {
        list: action.payload.conferenceRooms,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setDayNightStore(state, action) {
      state.dayNights = {
        list: action.payload.switches,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setCustomDestinationStore(state, action) {
      state.customDestinations = {
        list: action.payload.customDestinations,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setDispatcherStore(state, action) {
      state.dispatchers = {
        list: action.payload.dispatchers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setDistinctiveRingtoneStore(state, action) {
      // This list is generated by a v4 route
      state.distinctiveRingtones = {
        list: action.payload.distinctiveRingtones,
        meta: { ...action.payload.metadata, lastFetched: new Date().toString() },
      };
    },
    setGroupStore(state, action) {
      state.groups = {
        list: action.payload.groups,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setHolidaySetStore(state, action) {
      state.holidaySets = {
        list: action.payload.holidaySets,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setImageStore(state, action) {
      state.images = {
        list: action.payload.images,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setInboundRouteStore(state, action) {
      state.inboundRoutes = {
        list: action.payload.inboundRoutes,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setIntercomStore(state, action) {
      state.intercoms = {
        list: action.payload.intercoms,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setIvrStore(state, action) {
      state.ivrs = {
        list: action.payload.ivrs,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setLineKeyTemplateStore(state, action) {
      state.lineKeyTemplates = {
        list: action.payload.lineKeyTemplates,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setMusicOnHoldStore(state, action) {
      state.musicOnHolds = {
        list: action.payload.musiconhold,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setMobileStore(state, action) {
      state.mobile = {
        list: action.payload.sippeers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setOutboundRuleStore(state, action) {
      state.outboundRules = {
        list: action.payload.outboundRules,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setPhonebookStore(state, action) {
      state.phonebooks = {
        list: action.payload.phonebooks,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setProvisioningPhoneStore(state, action) {
      state.provisionings = {
        list: action.payload.phones,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setQueueStore(state, action) {
      state.queues = {
        list: action.payload.queues,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setRingtoneStore(state, action) {
      state.ringtones = {
        list: action.payload.ringtones,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setSoundStore(state, action) {
      state.sounds = {
        list: action.payload.sounds,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setTimetableStore(state, action) {
      state.timetables = {
        list: action.payload.timetables,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setUserStore(state, action) {
      state.users = {
        list: action.payload.sippeers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setVoicemailStore(state, action) {
      state.voicemails = {
        list: action.payload.voicemails,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setWebhookStore(state, action) {
      state.webhooks = {
        list: action.payload.webhooks,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },

    // Specialized lists
    setProvisionableAccountStore: (state, action) => {
      state.provisionableAccounts = {
        list: action.payload.sippeers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setProvisionablePhoneStore: (state, action) => {
      state.provisionablePhones = {
        list: action.payload.phones,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setQueueMemberStore: (state, action) => {
      state.queueMembers = {
        list: action.payload.data,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setPbxPhoneNumberStore: (state, action) => {
      state.phoneNumbers = {
        list: action.payload.phoneNumbers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },
    setAvailablePbxPhoneNumberStore: (state, action) => {
      state.availablePhoneNumbers = {
        list: action.payload.phoneNumbers,
        meta: { count: action.payload.count, lastFetched: new Date().toString() },
      };
    },

    // Manipulate lists
    updateImageStore: (state, action) => {
      state.images.list = state.images.list.map((_image) => {
        if (_image.id === action.payload.id) {
          _image.updatedAt = new Date().getTime();
        }

        return _image;
      });
    },
    updateSoundStore: (state, action) => {
      state.sounds.list = state.sounds.list.map((sound) => {
        if (sound.id === action.payload.id) {
          sound.updatedAt = new Date().getTime();
        }

        return sound;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase("root/clearPbxData", () => {
        return initialState;
      })
      .addCase("root/clearAuthData", () => {
        return initialState;
      });
  },
});

export const {
  setAccountStore,
  setApiTokenStore,
  setAvailablePbxPhoneNumberStore,
  setBlocklistStore,
  setCallLog,
  setCallParkingStore,
  setConferenceStore,
  setContactStore,
  setCustomDestinationStore,
  setDayNightStore,
  setDistinctiveRingtoneStore,
  setFlexDeskLog,
  setGroupStore,
  setHasRunFetchCheck,
  setHolidaySetStore,
  setImageStore,
  setInboundRouteStore,
  setIntercomStore,
  setIvrStore,
  setLineKeyTemplateStore,
  setMobileStore,
  setMusicOnHoldStore,
  setOutboundRuleStore,
  setPbx,
  setPbxAccounts,
  setPbxPhoneNumberStore,
  setPbxUserStore,
  setPbxWithReset,
  setPhonebookStore,
  setProvisionableAccountStore,
  setProvisionablePhoneStore,
  setProvisioningPhoneStore,
  setQueueLog,
  setQueueMemberLog,
  setQueueMemberStore,
  setQueueStore,
  setRingtoneStore,
  setSoundStore,
  setTagStore,
  setTimetableStore,
  setUserStore,
  setAllUserStore,
  setVoicemailStore,
  setWebhookLog,
  setWebhookStore,
  updateImageStore,
  updateSoundStore,
} = actions;

export default reducer;
